import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dahhgvboh653ivfvnvm0";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, typeof value === "string" ? value : "Uncollectable");
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-primary-900 text-background-50">
      <div className="mx-auto w-full max-w-[1180px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-label tracking-[0.22em] text-accent-300">
              <i className="ri-mail-send-line text-xs" />
              SUBSCRIBE · 订阅深扒
            </span>
            <h2 className="mt-3 font-heading text-2xl font-black leading-snug md:text-[32px]">
              每期深扒，只发一次
            </h2>
            <p className="mt-3 max-w-lg text-xs leading-relaxed text-primary-100 md:text-sm">
              订阅后，新一期整理好的线索、时间线与合集更新会直接送到你的邮箱。可随时退订，不收无关推送。
            </p>

            <ul className="mt-6 space-y-2.5">
              {["每期标题与线索摘要", "新合集上线提醒", "更正与新增记录同步"].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-xs text-primary-100 md:text-sm">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-check-line text-sm text-accent-300" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6">
            <form
              id="subscribe-form"
              data-readdy-form
              action={FORM_URL}
              method="post"
              onSubmit={handleSubmit}
              className="rounded-lg border border-primary-700 bg-primary-950/60 p-5 md:p-6"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="subscribe-email"
                    className="mb-2 block text-xs font-medium text-primary-100"
                  >
                    邮箱地址
                  </label>
                  <input
                    id="subscribe-email"
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="w-full rounded-md border border-primary-700 bg-primary-900/70 px-3.5 py-2.5 text-sm text-background-50 placeholder:text-primary-300 focus:border-accent-400 focus:outline-none"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="subscribe-interest"
                    className="mb-2 block text-xs font-medium text-primary-100"
                  >
                    想先看哪一类
                  </label>
                  <select
                    id="subscribe-interest"
                    name="interest"
                    defaultValue="深扒精选"
                    className="w-full cursor-pointer rounded-md border border-primary-700 bg-primary-900/70 px-3.5 py-2.5 text-sm text-background-50 focus:border-accent-400 focus:outline-none"
                  >
                    <option value="深扒精选">深扒精选</option>
                    <option value="线索时间线">线索时间线</option>
                    <option value="合集索引">合集索引</option>
                    <option value="热度榜单">热度榜单</option>
                  </select>
                </div>
              </div>

              <div className="field-extra-note" aria-hidden="true">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {status === "loading" ? "提交中…" : "订阅深扒"}
                <i className="ri-arrow-right-line text-base" />
              </button>

              {status === "success" ? (
                <p className="mt-4 flex items-center gap-2 text-xs text-accent-200">
                  <i className="ri-checkbox-circle-line text-base" />
                  订阅成功，下一期深扒会送到你的邮箱。
                </p>
              ) : null}

              {status === "error" ? (
                <p className="mt-4 flex items-center gap-2 text-xs text-primary-200">
                  <i className="ri-error-warning-line text-base" />
                  {formError}
                </p>
              ) : null}

              <p className="mt-4 text-[11px] leading-relaxed text-primary-300">
                提交即表示同意接收本栏目的内容更新邮件。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}